import React, { useState, useEffect, useCallback } from 'react'
import Swal from 'sweetalert2'
import type { TenantThemeConfig } from '../types/theme'
import * as authService from '../services/authService'
import { getWithdrawalSettings } from '../services/withdrawalSettingsService'

interface AssetConfig {
  CDN_BASE_URL: string
  CSS_PATH: string
  IMG_PATH: string
  FONTS_PATH: string
}

interface ModernMemberPageProps {
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
}

/** แถวข้อมูลในกล่อง — label ซ้าย, value หรือปุ่มขวา */
const InfoRow: React.FC<{
  label: string
  value?: React.ReactNode
  action?: React.ReactNode
}> = ({ label, value, action }) => (
  <div className="row py-3 bdc_b_secondary">
    <div className="col-12 col-md-6">
      <h5 className="c_quinary font_md">{label} :</h5>
    </div>
    <div className="col-12 col-md-6 d-flex flex-wrap align-items-center justify-content-md-end gap-2">
      {value != null && <h5 className="c_tertiary mb-0">{value}</h5>}
      {action}
    </div>
  </div>
)

/** รายการธนาคารสำหรับเลือก (รหัส + ชื่อ) */
const BANK_OPTIONS: { code: string; name: string }[] = [
  { code: 'KBANK', name: 'ธนาคารกสิกรไทย' },
  { code: 'SCB', name: 'ธนาคารไทยพาณิชย์' },
  { code: 'BBL', name: 'ธนาคารกรุงเทพ' },
  { code: 'KTB', name: 'ธนาคารกรุงไทย' },
  { code: 'BAY', name: 'ธนาคารกรุงศรีอยุธยา' },
  { code: 'TTB', name: 'ธนาคารทหารไทยธนชาต' },
  { code: 'GSB', name: 'ธนาคารออมสิน' },
  { code: 'TBANK', name: 'ธนาคารธนชาต' },
  { code: 'UOBT', name: 'ธนาคารยูโอบี' },
  { code: 'CIMBT', name: 'ธนาคารซีไอเอ็มบี ไทย' },
]

const hasTextValue = (value?: string | null) => Boolean(String(value || '').trim())
const isUnsetLineId = (value?: string | null) => {
  const normalized = String(value || '').trim().toLowerCase()
  return !normalized || normalized === 'system_default'
}

/** หน้าข้อมูลบัญชี (member) — ดึงข้อมูลจาก API user-info, เพิ่มได้ 1 ครั้งเท่านั้น */
const ModernMemberPage: React.FC<ModernMemberPageProps> = () => {
  const [showEditUserModal, setShowEditUserModal] = useState(false)
  const [editTarget, setEditTarget] = useState<'profile' | 'bank' | 'trueWallet'>('profile')
  const [userInfo, setUserInfo] = useState<authService.UserInfoFull | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  // ตั้งค่าต่อ tenant: อนุญาตให้ลูกค้ากรอก PromptPay เองหรือไม่ (default เปิด)
  const [promptPayInputEnabled, setPromptPayInputEnabled] = useState(true)
  // ค่าฟอร์มในโมดัล (ผูกกับข้อมูลจริง)
  const [editName, setEditName] = useState('')
  const [editLine, setEditLine] = useState('')
  const [editBankCode, setEditBankCode] = useState('')
  const [editBankName, setEditBankName] = useState('')
  const [editAccountNumber, setEditAccountNumber] = useState('')
  const [editAccountName, setEditAccountName] = useState('')
  const [editTrueName, setEditTrueName] = useState('')
  const [editTrueMobile, setEditTrueMobile] = useState('')
  const [editTrueId, setEditTrueId] = useState('')

  /** ไม่ล็อก — ให้เพิ่ม/แก้ไขข้อมูลได้ตลอด (รวมบัญชีธนาคารและ TrueMoney) */
  const isProfileLocked = false

  const loadUserInfo = useCallback(async () => {
    if (!authService.isAuthenticated()) {
      setLoading(false)
      return
    }
    setLoading(true)
    setLoadError(null)
    const data = await authService.fetchUserInfoFull()
    setUserInfo(data)
    setLoading(false)
    if (!data) setLoadError('โหลดข้อมูลไม่สำเร็จ')
  }, [])

  const openChangePasswordDialog = useCallback(() => {
    const getThemedSwalOptions = () => ({
      background: 'linear-gradient(180deg, rgba(10,18,40,0.98) 0%, rgba(9,14,30,0.98) 100%)',
      color: '#f8fbff',
      confirmButtonColor: '#3d6fff',
      backdrop: 'rgba(4, 10, 27, 0.82)',
      showCloseButton: true,
      customClass: {
        popup: 'shadow-lg border-0 rounded-4',
        title: 'c_tertiary fw-600',
        htmlContainer: 'text-start',
      },
      didOpen: (popup: HTMLElement) => {
        popup.style.border = '1px solid rgba(86, 124, 255, 0.18)'
        popup.style.boxShadow = '0 30px 80px rgba(0, 0, 0, 0.45)'

        const labels = popup.querySelectorAll('label')
        labels.forEach((label) => {
          const el = label as HTMLElement
          el.style.color = 'rgba(255,255,255,0.76)'
          el.style.fontWeight = '500'
        })

        const inputs = popup.querySelectorAll('input')
        inputs.forEach((input) => {
          const el = input as HTMLInputElement
          el.style.minHeight = '46px'
          el.style.borderRadius = '14px'
          el.style.background = 'rgba(8, 16, 34, 0.88)'
          el.style.border = '1px solid rgba(255,255,255,0.1)'
          el.style.color = 'rgba(255,255,255,0.92)'
          el.style.boxShadow = 'none'
        })

        const confirmBtn = Swal.getConfirmButton()
        if (confirmBtn) {
          confirmBtn.style.border = 'none'
          confirmBtn.style.borderRadius = '999px'
          confirmBtn.style.padding = '0.7rem 1.25rem'
          confirmBtn.style.background = 'linear-gradient(135deg, #3d6fff 0%, #5f6bff 45%, #00b8ff 100%)'
          confirmBtn.style.boxShadow = '0 14px 30px rgba(61, 111, 255, 0.32)'
        }

        const cancelBtn = Swal.getCancelButton()
        if (cancelBtn) {
          cancelBtn.style.borderRadius = '999px'
          cancelBtn.style.padding = '0.7rem 1.25rem'
          cancelBtn.style.border = '1px solid rgba(255,255,255,0.16)'
          cancelBtn.style.background = 'rgba(255,255,255,0.06)'
          cancelBtn.style.color = 'rgba(255,255,255,0.82)'
        }
      },
    })

    Swal.fire({
      title: 'เปลี่ยนรหัสผ่าน',
      html: `
        <div class="text-start">
          <div style="display:flex; align-items:flex-start; gap:12px; padding:12px 14px; margin-bottom:16px; border-radius:16px; background:rgba(61,111,255,0.12); border:1px solid rgba(95,140,255,0.2);">
            <i class="fas fa-lock" style="color:#7cb8ff; margin-top:3px;"></i>
            <div style="font-size:0.92rem; color:rgba(255,255,255,0.76);">
              เพื่อความปลอดภัย กรุณากรอกรหัสผ่านเดิมก่อนตั้งรหัสผ่านใหม่
            </div>
          </div>
          <label class="form-label small mb-2">รหัสผ่านเดิม</label>
          <input id="swal-current" type="password" class="form-control form-control-lg mb-3" placeholder="โปรดใส่รหัสผ่านเดิม" autocomplete="current-password" />
          <label class="form-label small mb-2">รหัสผ่านใหม่ (อย่างน้อย 6 ตัวอักษร)</label>
          <input id="swal-new" type="password" class="form-control form-control-lg mb-3" placeholder="โปรดใส่รหัสผ่านใหม่" autocomplete="new-password" minlength="6" />
          <label class="form-label small mb-2">ยืนยันรหัสผ่านใหม่</label>
          <input id="swal-confirm" type="password" class="form-control form-control-lg" placeholder="ยืนยันรหัสผ่านใหม่" autocomplete="new-password" minlength="6" />
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: '<i class="fas fa-key me-1"></i> เปลี่ยนรหัสผ่าน',
      cancelButtonText: 'ปิด',
      width: '460px',
      padding: '1.35rem',
      ...getThemedSwalOptions(),
      preConfirm: () => {
        const popup = Swal.getPopup()
        if (!popup) return false
        const current = (popup.querySelector('#swal-current') as HTMLInputElement)?.value?.trim()
        const newPw = (popup.querySelector('#swal-new') as HTMLInputElement)?.value?.trim()
        const confirm = (popup.querySelector('#swal-confirm') as HTMLInputElement)?.value?.trim()
        if (!current) {
          Swal.showValidationMessage('กรุณากรอกรหัสผ่านเดิม')
          return false
        }
        if (!newPw || newPw.length < 6) {
          Swal.showValidationMessage('รหัสผ่านใหม่ต้องมีอย่างน้อย 6 ตัวอักษร')
          return false
        }
        if (newPw !== confirm) {
          Swal.showValidationMessage('รหัสผ่านใหม่กับยืนยันไม่ตรงกัน')
          return false
        }
        return { currentPassword: current, newPassword: newPw }
      },
    }).then(async (result) => {
      if (!result.isConfirmed || !result.value) return
      const { currentPassword, newPassword } = result.value
      const res = await authService.changePassword(currentPassword, newPassword)
      if (res.success) {
        await Swal.fire({
          icon: 'success',
          title: 'เปลี่ยนรหัสผ่านสำเร็จ',
          text: 'กรุณาเข้าสู่ระบบใหม่ด้วยรหัสผ่านใหม่',
          ...getThemedSwalOptions(),
        })
        await authService.logout()
        window.location.hash = '/'
        window.location.reload()
      } else {
        await Swal.fire({
          icon: 'error',
          title: 'เปลี่ยนรหัสผ่านไม่สำเร็จ',
          text: res.error || 'กรุณาลองใหม่อีกครั้ง',
          ...getThemedSwalOptions(),
        })
      }
    })
  }, [])

  useEffect(() => {
    loadUserInfo()
  }, [loadUserInfo])

  useEffect(() => {
    let cancelled = false
    getWithdrawalSettings()
      .then((s) => {
        if (!cancelled) setPromptPayInputEnabled(s.promptPayFeatures?.customerInputEnabled !== false)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  // เมื่อเปิดโมดัล ให้ดึงค่าจาก userInfo มาใส่ฟอร์ม
  useEffect(() => {
    if (showEditUserModal && userInfo) {
      setEditName(userInfo.name || '')
      setEditLine(isUnsetLineId(userInfo.lineAccountId) ? '' : userInfo.lineAccountId || '')
      const bank = userInfo.bankAccount?.[0]
      setEditBankCode(bank?.bankCode || '')
      setEditBankName(bank?.bankName || '')
      setEditAccountNumber(bank?.accountNumber || '')
      setEditAccountName(bank?.accountName || '')
      const tw = userInfo.trueWallet?.[0]
      setEditTrueName(tw?.trueWalletName || '')
      setEditTrueMobile(tw?.trueWalletMobile || '')
      setEditTrueId(tw?.trueWalletId || '')
    }
  }, [showEditUserModal, userInfo])

  const openEditModal = useCallback((target: 'profile' | 'bank' | 'trueWallet') => {
    if (isProfileLocked) {
      Swal.fire({
        icon: 'info',
        title: 'ไม่สามารถแก้ไขได้',
        text: 'คุณได้กรอกข้อมูลสมาชิกไว้แล้ว ไม่สามารถแก้ไขได้ เพื่อความปลอดภัยและป้องกันการขโมยเงิน',
        confirmButtonColor: '#0d6efd',
      })
      return
    }
    setEditTarget(target)
    setShowEditUserModal(true)
  }, [isProfileLocked])

  const user = authService.getCurrentUser()
  const displayUsername = userInfo?.username ?? user?.username ?? user?.mobile ?? '–'
  const displayMobile = userInfo?.mobile ?? user?.mobile ?? user?.username ?? '–'
  const normalizedLineId = isUnsetLineId(userInfo?.lineAccountId) ? '' : String(userInfo?.lineAccountId || '').trim()
  const primaryBank = userInfo?.bankAccount?.find((a) => a.isActive !== false) ?? userInfo?.bankAccount?.[0]
  const primaryTrueWallet = userInfo?.trueWallet?.[0]
  const canEditName = !hasTextValue(userInfo?.name)
  const canEditLine = !hasTextValue(normalizedLineId)
  const canEditBank = !primaryBank
  const canEditTrueWallet = !primaryTrueWallet
  const allowNameEdit = editTarget === 'profile' && canEditName
  const allowLineEdit = editTarget === 'profile' && canEditLine
  const allowBankEdit = editTarget === 'bank' && canEditBank
  const allowTrueWalletEdit = editTarget === 'trueWallet' && canEditTrueWallet
  const hasEditableSection = allowNameEdit || allowLineEdit || allowBankEdit || allowTrueWalletEdit

  const handleSubmitEditProfile = useCallback(async () => {
    const name = editName.trim()
    const line = editLine.trim()
    const bankCode = editBankCode.trim()
    const bankName = editBankName.trim() || BANK_OPTIONS.find((b) => b.code === editBankCode)?.name || ''
    const accountNumber = editAccountNumber.replace(/\D/g, '')
    const accountName = editAccountName.trim()
    const trueName = editTrueName.trim()
    const trueMobile = editTrueMobile.replace(/\D/g, '')
    const trueId = editTrueId.trim()

    if (allowNameEdit && !name) {
      Swal.fire({ icon: 'warning', title: 'กรุณากรอกชื่อ-นามสกุล', confirmButtonColor: '#0d6efd' })
      return
    }
    if (allowBankEdit && (!bankCode || !accountNumber || accountNumber.length < 10 || !accountName)) {
      Swal.fire({
        icon: 'warning',
        title: 'กรุณาระบุบัญชีธนาคาร',
        text: 'เลือกธนาคาร หมายเลขบัญชี (10–12 หลัก) และชื่อบัญชี',
        confirmButtonColor: '#0d6efd',
      })
      return
    }
    const hasAnyTrueWalletInput = Boolean(trueId || trueName || trueMobile)
    if (allowTrueWalletEdit && hasAnyTrueWalletInput && (!trueName || !trueMobile || !trueId)) {
      Swal.fire({
        icon: 'warning',
        title: 'กรุณากรอกข้อมูล TrueMoney ให้ครบ',
        text: 'ระบุชื่อ เบอร์มือถือ 10 หลัก และ Wallet ID ให้ครบถ้วน',
        confirmButtonColor: '#0d6efd',
      })
      return
    }
    if (allowTrueWalletEdit && trueMobile && trueMobile.length !== 10) {
      Swal.fire({ icon: 'warning', title: 'เบอร์ TrueMoney ต้อง 10 หลัก', confirmButtonColor: '#0d6efd' })
      return
    }

    setSubmitting(true)
    const payload: Parameters<typeof authService.updateUserInfoFull>[0] = {}
    if (allowNameEdit && name) payload.name = name
    if (allowLineEdit && line) payload.lineAccountId = line
    if (allowBankEdit) {
      payload.bankAccount = [{ bankCode, bankName: bankName || bankCode, accountNumber, accountName }]
    }
    if (allowTrueWalletEdit && hasAnyTrueWalletInput) {
      payload.trueWallet = [
        {
          trueWalletId: trueId,
          trueWalletName: trueName,
          trueWalletMobile: trueMobile,
        },
      ]
    }
    if (Object.keys(payload).length === 0) {
      setSubmitting(false)
      await Swal.fire({
        icon: 'info',
        title: 'ยังไม่มีข้อมูลใหม่สำหรับบันทึก',
        text: 'กรอกเฉพาะช่องที่ยังว่างก่อนกดบันทึก',
        confirmButtonColor: '#0d6efd',
      })
      return
    }
    const res = await authService.updateUserInfoFull(payload)
    setSubmitting(false)
    if (res.success) {
      setShowEditUserModal(false)
      await Swal.fire({ icon: 'success', title: 'บันทึกข้อมูลสำเร็จ', confirmButtonColor: '#0d6efd' })
      loadUserInfo()
    } else {
      await Swal.fire({ icon: 'error', title: 'บันทึกไม่สำเร็จ', text: res.error, confirmButtonColor: '#0d6efd' })
    }
  }, [editName, editLine, editBankCode, editBankName, editAccountNumber, editAccountName, editTrueName, editTrueMobile, editTrueId, loadUserInfo, allowNameEdit, allowLineEdit, allowBankEdit, allowTrueWalletEdit])

  // เพิ่ม PromptPay ลงบัญชีธนาคารหลัก (เซ็ตได้ครั้งเดียว)
  const handleAddPromptPay = useCallback(async () => {
    const { value, isConfirmed } = await Swal.fire({
      icon: 'info',
      title: 'เพิ่ม PromptPay',
      input: 'text',
      inputLabel: 'เบอร์พร้อมเพย์ 10 หลัก หรือเลขบัตรประชาชน 13 หลัก',
      inputPlaceholder: 'กรอกเลขพร้อมเพย์',
      inputAttributes: { inputmode: 'numeric', maxlength: '13' },
      showCancelButton: true,
      confirmButtonText: 'บันทึก',
      cancelButtonText: 'ยกเลิก',
      confirmButtonColor: '#0d6efd',
      inputValidator: (v: string) => {
        const digits = String(v || '').replace(/\D/g, '')
        return /^(\d{10}|\d{13})$/.test(digits) ? undefined : 'กรอกเบอร์ 10 หลัก หรือเลขบัตรประชาชน 13 หลัก'
      },
    })
    if (!isConfirmed) return
    const digits = String(value || '').replace(/\D/g, '')
    setSubmitting(true)
    const res = await authService.setBankPromptPay(digits)
    setSubmitting(false)
    if (res.success) {
      await Swal.fire({ icon: 'success', title: 'บันทึก PromptPay สำเร็จ', confirmButtonColor: '#0d6efd' })
      loadUserInfo()
    } else {
      await Swal.fire({ icon: 'error', title: 'บันทึกไม่สำเร็จ', text: res.error, confirmButtonColor: '#0d6efd' })
    }
  }, [loadUserInfo])

  if (loading) {
    return (
      <div className="content">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center py-5 c_tertiary">กำลังโหลดข้อมูล...</div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="content">
      <div className="container">
        <div className="row">
          <div className="col-12 col-lg-10 offset-lg-1">
            {loadError && (
              <div className="alert alert-warning mb-3" role="alert">
                {loadError}
                <button type="button" className="btn btn-sm btn-outline-secondary ms-2" onClick={loadUserInfo}>
                  โหลดใหม่
                </button>
              </div>
            )}
            <div className="c_quinary text-center">
              <h3 className="c_tertiary font_md">ชื่อผู้ใช้และรหัสผ่าน</h3>
            </div>

            <div className="light_sd border_10 p-3 p-md-4 bg_op_secondary bg_blur mt-3 mb-3">
              <InfoRow label="เบอร์ที่สมัคร" value={displayMobile} />
              <InfoRow label="Username" value={displayUsername} />
              <InfoRow
                label="Password"
                value="***********"
                action={
                  <a
                    href="#"
                    className="c_tertiary ms-auto txt_links_primary"
                    onClick={(e) => {
                      e.preventDefault()
                      openChangePasswordDialog()
                    }}
                  >
                    <span>เปลี่ยนรหัสผ่าน</span>
                  </a>
                }
              />
            </div>

            <div className="c_quinary text-center">
              <h3 className="c_tertiary font_md">ข้อมูลผู้ใช้</h3>
            </div>

            <div className="light_sd border_10 p-3 p-md-4 bg_op_secondary bg_blur mt-3 mb-3">
              <InfoRow label="ชื่อ-นามสกุล" value={userInfo?.name || '–'} />
              <InfoRow
                label="Line"
                value={normalizedLineId || undefined}
                action={
                  canEditLine ? (
                    <a
                      href="#"
                      className="ms-auto link_pmr px-3 box_row justify-content-center font_rg bg_primary border_10 ins_sd"
                      style={{ width: 'fit-content' }}
                      onClick={(e) => {
                        e.preventDefault()
                        openEditModal('profile')
                      }}
                    >
                      <span className="c_tertiary">เพิ่ม</span>
                      <i className="fas fa-plus c_tertiary ms-1" />
                    </a>
                  ) : undefined
                }
              />
              <InfoRow
                label="หมายเลขบัญชี"
                value={primaryBank?.accountNumber ? primaryBank.accountNumber : undefined}
                action={
                  !primaryBank ? (
                    <a
                      href="#"
                      className="ms-auto link_pmr px-3 box_row justify-content-center font_rg bg_primary border_10 ins_sd"
                      style={{ width: 'fit-content' }}
                      onClick={(e) => {
                        e.preventDefault()
                        openEditModal('bank')
                      }}
                    >
                      <span className="c_tertiary">เพิ่ม</span>
                      <i className="fas fa-plus c_tertiary ms-1" />
                    </a>
                  ) : undefined
                }
              />
              <InfoRow label="ธนาคาร" value={primaryBank?.bankName || '–'} />
              {(promptPayInputEnabled || primaryBank?.promptPayId) && (
              <InfoRow
                label="PromptPay"
                value={primaryBank?.promptPayId ? primaryBank.promptPayId : undefined}
                action={
                  promptPayInputEnabled && primaryBank && !primaryBank.promptPayId ? (
                    <a
                      href="#"
                      className="ms-auto link_pmr px-3 box_row justify-content-center font_rg bg_primary border_10 ins_sd"
                      style={{ width: 'fit-content' }}
                      onClick={(e) => {
                        e.preventDefault()
                        void handleAddPromptPay()
                      }}
                    >
                      <span className="c_tertiary">เพิ่ม</span>
                      <i className="fas fa-plus c_tertiary ms-1" />
                    </a>
                  ) : undefined
                }
              />
              )}
              <InfoRow
                label="TrueMoney(ชื่อ)"
                value={primaryTrueWallet?.trueWalletName ? primaryTrueWallet.trueWalletName : undefined}
                action={
                  !primaryTrueWallet ? (
                    <a
                      href="#"
                      className="ms-auto link_pmr px-3 box_row justify-content-center font_rg bg_primary border_10 ins_sd"
                      style={{ width: 'fit-content' }}
                      onClick={(e) => {
                        e.preventDefault()
                        openEditModal('trueWallet')
                      }}
                    >
                      <span className="c_tertiary">เพิ่ม</span>
                      <i className="fas fa-plus c_tertiary ms-1" />
                    </a>
                  ) : undefined
                }
              />
              <InfoRow
                label="TrueMoney(เบอร์)"
                value={primaryTrueWallet?.trueWalletMobile || '–'}
              />
              <InfoRow
                label="TrueMoney(ID)"
                value={primaryTrueWallet?.trueWalletId || '–'}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Modal แก้ไขข้อมูลสมาชิก — กรอกได้ 1 ครั้งเท่านั้น หลังมีข้อมูลแล้วจะล็อก */}
      {showEditUserModal && (
        <>
          <div
            className="modal-backdrop fade show"
            style={{ zIndex: 1050, background: 'rgba(4, 10, 27, 0.82)', backdropFilter: 'blur(8px)' }}
            onClick={() => setShowEditUserModal(false)}
            aria-hidden="true"
          />
          <div className="modal fade show" style={{ display: 'block', zIndex: 1055 }} tabIndex={-1} aria-labelledby="edituserModalLabel" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg">
              <div
                className="modal-content border-0 overflow-hidden"
                style={{
                  borderRadius: '28px',
                  background: 'linear-gradient(180deg, rgba(10,18,40,0.98) 0%, rgba(9,14,30,0.98) 100%)',
                  boxShadow: '0 30px 80px rgba(0, 0, 0, 0.45)',
                  border: '1px solid rgba(86, 124, 255, 0.18)',
                }}
              >
                <div
                  className="modal-header border-0"
                  style={{
                    background: 'linear-gradient(135deg, rgba(49, 94, 255, 0.22), rgba(13, 187, 255, 0.08))',
                    padding: '1.25rem 1.25rem 1rem',
                  }}
                >
                  <div className="w-100 d-flex align-items-start justify-content-between gap-3">
                    <div>
                      <div
                        className="small mb-2"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.45rem',
                          padding: '0.35rem 0.7rem',
                          borderRadius: '999px',
                          background: 'rgba(255,255,255,0.08)',
                          color: 'rgba(255,255,255,0.82)',
                        }}
                      >
                        <i className="fas fa-user-edit" />
                        อัปเดตข้อมูลสมาชิก
                      </div>
                      <h5 className="modal-title fw-600 c_tertiary mb-2" id="edituserModalLabel">
                        แก้ไขข้อมูลสมาชิก
                      </h5>
                      <p className="mb-0 small" style={{ color: 'rgba(255,255,255,0.72)' }}>
                        ช่องที่มีข้อมูลอยู่แล้วจะถูกล็อกอัตโนมัติ และบันทึกเฉพาะส่วนที่ยังว่างเท่านั้น
                      </p>
                    </div>
                    <button
                      type="button"
                      className="btn-close btn-close-white"
                      aria-label="ปิด"
                      onClick={() => setShowEditUserModal(false)}
                    />
                  </div>
                </div>
                <div className="modal-body pt-3 pt-md-4" style={{ padding: '1.25rem' }}>
                  <div
                    className="mb-3 border_10"
                    style={{
                      padding: '1rem',
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.07)',
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
                      <div>
                        <h6 className="c_tertiary fw-600 mb-1">ข้อมูลพื้นฐาน</h6>
                        <p className="small mb-0" style={{ color: 'rgba(255,255,255,0.58)' }}>
                          ชื่อ-นามสกุลและ Line ID
                        </p>
                      </div>
                      <span
                        className="small"
                        style={{
                          padding: '0.35rem 0.65rem',
                          borderRadius: '999px',
                          background: allowNameEdit || allowLineEdit ? 'rgba(34, 197, 94, 0.14)' : 'rgba(255,255,255,0.08)',
                          color: allowNameEdit || allowLineEdit ? '#9af0b2' : 'rgba(255,255,255,0.68)',
                        }}
                      >
                        {allowNameEdit || allowLineEdit ? 'เพิ่มข้อมูลได้' : canEditName || canEditLine ? 'เพิ่มได้จากส่วนข้อมูลผู้ใช้' : 'มีข้อมูลแล้ว'}
                      </span>
                    </div>
                    <div className="mb-3">
                      <label className="form-label small fw-500 mb-2" style={{ color: 'rgba(255,255,255,0.8)' }}>
                        ชื่อ-นามสกุล
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="ระบุชื่อ-นามสกุลให้ถูกต้อง"
                        value={editName}
                        disabled={!allowNameEdit}
                        readOnly={!allowNameEdit}
                        onChange={(e) => setEditName(e.target.value)}
                        style={{
                          minHeight: '46px',
                          borderRadius: '14px',
                          background: !allowNameEdit ? 'rgba(255,255,255,0.08)' : 'rgba(8, 16, 34, 0.88)',
                          border: '1px solid rgba(255,255,255,0.1)',
                          color: 'rgba(255,255,255,0.92)',
                        }}
                      />
                      {!allowNameEdit && (
                        <div className="small mt-2" style={{ color: 'rgba(255,255,255,0.52)' }}>
                          {canEditName ? 'รอบนี้ไม่ได้เปิดแก้ส่วนข้อมูลพื้นฐาน ให้เพิ่มจากปุ่มของส่วนนั้นโดยตรง' : 'ข้อมูลนี้มีอยู่แล้ว จึงไม่สามารถแก้ไขได้'}
                        </div>
                      )}
                    </div>
                    <div className="mb-0">
                      <label className="form-label small fw-500 mb-2" style={{ color: 'rgba(255,255,255,0.8)' }}>
                        Line ID
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="ระบุ Line ID"
                        value={editLine}
                        disabled={!allowLineEdit}
                        readOnly={!allowLineEdit}
                        onChange={(e) => setEditLine(e.target.value)}
                        style={{
                          minHeight: '46px',
                          borderRadius: '14px',
                          background: !allowLineEdit ? 'rgba(255,255,255,0.08)' : 'rgba(8, 16, 34, 0.88)',
                          border: '1px solid rgba(255,255,255,0.1)',
                          color: 'rgba(255,255,255,0.92)',
                        }}
                      />
                      {!allowLineEdit && (
                        <div className="small mt-2" style={{ color: 'rgba(255,255,255,0.52)' }}>
                          {canEditLine ? 'รอบนี้ไม่ได้เปิดเพิ่ม Line ID เพื่อป้องกันการแก้หลายส่วนพร้อมกัน' : 'มี Line ID อยู่แล้ว ระบบจะเก็บข้อมูลเดิมไว้'}
                        </div>
                      )}
                    </div>
                  </div>

                  <div
                    className="mb-3 border_10"
                    style={{
                      padding: '1rem',
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.07)',
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
                      <div>
                        <h6 className="c_tertiary fw-600 mb-1">บัญชีธนาคาร</h6>
                        <p className="small mb-0" style={{ color: 'rgba(255,255,255,0.58)' }}>
                          ใช้สำหรับรับเงินและยืนยันตัวตนทางการเงิน
                        </p>
                      </div>
                      <span
                        className="small"
                        style={{
                          padding: '0.35rem 0.65rem',
                          borderRadius: '999px',
                          background: allowBankEdit ? 'rgba(34, 197, 94, 0.14)' : 'rgba(255,255,255,0.08)',
                          color: allowBankEdit ? '#9af0b2' : 'rgba(255,255,255,0.68)',
                        }}
                      >
                        {allowBankEdit ? 'เพิ่มข้อมูลได้' : canEditBank ? 'เพิ่มได้จากปุ่มบัญชีธนาคาร' : 'มีข้อมูลแล้ว'}
                      </span>
                    </div>
                    <div className="row g-3 mb-3">
                      <div className="col-12 col-md-6">
                        <label className="form-label small mb-2" style={{ color: 'rgba(255,255,255,0.72)' }}>
                          ธนาคาร
                        </label>
                        <select
                          className="form-select"
                          value={editBankCode}
                          disabled={!allowBankEdit}
                          onChange={(e) => {
                            const code = e.target.value
                            setEditBankCode(code)
                            const opt = BANK_OPTIONS.find((b) => b.code === code)
                            if (opt) setEditBankName(opt.name)
                          }}
                          style={{
                            minHeight: '46px',
                            borderRadius: '14px',
                            background: !allowBankEdit ? 'rgba(255,255,255,0.08)' : 'rgba(8, 16, 34, 0.88)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            color: 'rgba(255,255,255,0.92)',
                          }}
                        >
                          <option value="">เลือกธนาคาร</option>
                          {BANK_OPTIONS.map((b) => (
                            <option key={b.code} value={b.code}>{b.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label small mb-2" style={{ color: 'rgba(255,255,255,0.72)' }}>
                          เลขบัญชี
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="10-12 หลัก"
                          value={editAccountNumber}
                          disabled={!allowBankEdit}
                          readOnly={!allowBankEdit}
                          onChange={(e) => setEditAccountNumber(e.target.value.replace(/\D/g, '').slice(0, 12))}
                          style={{
                            minHeight: '46px',
                            borderRadius: '14px',
                            background: !allowBankEdit ? 'rgba(255,255,255,0.08)' : 'rgba(8, 16, 34, 0.88)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            color: 'rgba(255,255,255,0.92)',
                          }}
                        />
                      </div>
                    </div>
                    <div className="mb-0">
                      <label className="form-label small mb-2" style={{ color: 'rgba(255,255,255,0.72)' }}>
                        ชื่อบัญชี
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="ชื่อบนบัญชีธนาคาร"
                        value={editAccountName}
                        disabled={!allowBankEdit}
                        readOnly={!allowBankEdit}
                        onChange={(e) => setEditAccountName(e.target.value)}
                        style={{
                          minHeight: '46px',
                          borderRadius: '14px',
                          background: !allowBankEdit ? 'rgba(255,255,255,0.08)' : 'rgba(8, 16, 34, 0.88)',
                          border: '1px solid rgba(255,255,255,0.1)',
                          color: 'rgba(255,255,255,0.92)',
                        }}
                      />
                      {!allowBankEdit && (
                        <div className="small mt-2" style={{ color: 'rgba(255,255,255,0.52)' }}>
                          {canEditBank ? 'รอบนี้ไม่ได้เปิดเพิ่มบัญชีธนาคาร ให้ใช้ปุ่มเพิ่มของส่วนบัญชีธนาคารโดยตรง' : 'ระบบล็อกข้อมูลบัญชีธนาคารที่มีอยู่แล้วไว้ให้เพื่อความปลอดภัย'}
                        </div>
                      )}
                    </div>
                  </div>

                  <div
                    className="border_10"
                    style={{
                      padding: '1rem',
                      background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.05), rgba(87, 151, 255, 0.04))',
                      border: '1px solid rgba(87, 151, 255, 0.18)',
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
                      <div>
                        <h6 className="c_tertiary fw-600 mb-1">TrueMoney Wallet</h6>
                        <p className="small mb-0" style={{ color: 'rgba(255,255,255,0.58)' }}>
                          ถ้ายังไม่มี สามารถเพิ่มได้เฉพาะส่วนนี้โดยไม่กระทบข้อมูลเดิมส่วนอื่น
                        </p>
                      </div>
                      <span
                        className="small"
                        style={{
                          padding: '0.35rem 0.65rem',
                          borderRadius: '999px',
                          background: allowTrueWalletEdit ? 'rgba(34, 197, 94, 0.14)' : 'rgba(255,255,255,0.08)',
                          color: allowTrueWalletEdit ? '#9af0b2' : 'rgba(255,255,255,0.68)',
                        }}
                      >
                        {allowTrueWalletEdit ? 'เพิ่มข้อมูลได้' : canEditTrueWallet ? 'เพิ่มได้จากปุ่ม TrueMoney' : 'มีข้อมูลแล้ว'}
                      </span>
                    </div>
                    <div className="mb-3">
                      <label className="form-label small mb-2" style={{ color: 'rgba(255,255,255,0.72)' }}>
                        ชื่อ-นามสกุล (TrueMoney)
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="ชื่อที่ลงทะเบียน True Money"
                        value={editTrueName}
                        disabled={!allowTrueWalletEdit}
                        readOnly={!allowTrueWalletEdit}
                        onChange={(e) => setEditTrueName(e.target.value)}
                        style={{
                          minHeight: '46px',
                          borderRadius: '14px',
                          background: !allowTrueWalletEdit ? 'rgba(255,255,255,0.08)' : 'rgba(8, 16, 34, 0.88)',
                          border: '1px solid rgba(255,255,255,0.1)',
                          color: 'rgba(255,255,255,0.92)',
                        }}
                      />
                    </div>
                    <div className="row g-3 mb-0">
                      <div className="col-12 col-md-6">
                        <label className="form-label small mb-2" style={{ color: 'rgba(255,255,255,0.72)' }}>
                          เบอร์มือถือ
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="10 หลัก"
                          value={editTrueMobile}
                          disabled={!allowTrueWalletEdit}
                          readOnly={!allowTrueWalletEdit}
                          onChange={(e) => setEditTrueMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                          style={{
                            minHeight: '46px',
                            borderRadius: '14px',
                            background: !allowTrueWalletEdit ? 'rgba(255,255,255,0.08)' : 'rgba(8, 16, 34, 0.88)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            color: 'rgba(255,255,255,0.92)',
                          }}
                        />
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label small mb-2" style={{ color: 'rgba(255,255,255,0.72)' }}>
                          Wallet ID
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="TrueMoney Wallet ID"
                          value={editTrueId}
                          disabled={!allowTrueWalletEdit}
                          readOnly={!allowTrueWalletEdit}
                          onChange={(e) => setEditTrueId(e.target.value)}
                          style={{
                            minHeight: '46px',
                            borderRadius: '14px',
                            background: !allowTrueWalletEdit ? 'rgba(255,255,255,0.08)' : 'rgba(8, 16, 34, 0.88)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            color: 'rgba(255,255,255,0.92)',
                          }}
                        />
                      </div>
                    </div>
                    {!allowTrueWalletEdit && (
                      <div className="small mt-2" style={{ color: 'rgba(255,255,255,0.52)' }}>
                        {canEditTrueWallet ? 'รอบนี้ไม่ได้เปิดเพิ่ม TrueMoney ให้ใช้ปุ่มเพิ่มของส่วน TrueMoney โดยตรง' : 'มีข้อมูล TrueMoney อยู่แล้ว ระบบจึงไม่เปิดให้แก้ไขทับข้อมูลเดิม'}
                      </div>
                    )}
                  </div>
                </div>
                <div
                  className="modal-footer border-0"
                  style={{ padding: '0 1.25rem 1.25rem', gap: '0.75rem' }}
                >
                  <button
                    type="button"
                    className="btn"
                    style={{
                      minWidth: '120px',
                      borderRadius: '999px',
                      border: '1px solid rgba(255,255,255,0.16)',
                      color: 'rgba(255,255,255,0.82)',
                      background: 'rgba(255,255,255,0.06)',
                    }}
                    onClick={() => setShowEditUserModal(false)}
                  >
                    ปิด
                  </button>
                  <button
                    type="button"
                    className="btn px-4"
                    style={{
                      minWidth: '170px',
                      borderRadius: '999px',
                      border: 'none',
                      background: 'linear-gradient(135deg, #3d6fff 0%, #5f6bff 45%, #00b8ff 100%)',
                      color: '#fff',
                      boxShadow: '0 14px 30px rgba(61, 111, 255, 0.32)',
                    }}
                    disabled={submitting || !hasEditableSection}
                    onClick={handleSubmitEditProfile}
                  >
                    {submitting ? 'กำลังบันทึก...' : 'บันทึกข้อมูลใหม่'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default ModernMemberPage
